import React from 'react'
import { AppState } from '../../stores/app'
import { AppService, DomainService } from '../../services/index'
import { Container } from 'typescript-ioc'
import { DomainDto } from '../../dtos/domain'
import { Button, Well } from '@pushly/aqe/lib/components'
import { ConfigProvider, Divider, Flex, Form, Skeleton, Space, Typography } from 'antd'
import { AppleFilled, EditOutlined } from '@ant-design/icons'
import { SendIntegrationChannel } from '../../interfaces/send-integrations'
import { simpleFormErrorNotification } from '../../_utils/utils'
import * as randomstring from 'randomstring'
import { SendIntegrationsDrawer } from './send-integrations-editor/send-integrations-drawer'
import { DomainSendIntegrationModel } from '../../models/send-integration-configurations/domain-send-integration.model'
import FirebaseIcon from '../icons/firebase-icon'
import { ApnsIntegrationWell } from './apns-integration-well'
import { NativeFcmIntegrationWell } from './native-fcm-integration-well'
import { getIntegrationsValuesUpdate } from './send-integrations-editor/helpers'
import { isDeliveryChannelEnabled } from '../../_utils/domain'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { withRouter } from 'react-router-dom'
import { RouteComponentProps } from 'react-router'
import { SETTINGS_WELL_FORM_LAYOUT } from '../../theme/constants'

interface ISendIntegrationsWellProps extends RouteComponentProps {
    domain: DomainDto
    loading?: boolean
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface ISendIntegrationsWellState {
    showDrawer: boolean
    drawerKey?: string
}

class SendIntegrationsWell extends React.Component<ISendIntegrationsWellProps, ISendIntegrationsWellState> {
    public state: ISendIntegrationsWellState = {
        showDrawer: false,
    }

    protected appState: AppState
    protected appSvc: AppService
    protected domainService: DomainService

    public constructor(props: ISendIntegrationsWellProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appSvc = Container.get(AppService)
        this.domainService = Container.get(DomainService)
    }

    public render() {
        const nativeFcmLegacyFlag = this.appState?.flags.findActive('FEAT_SEND_INT_NATIVE_FCM_LEGACY')?.getKey()
        const hasNativeFcmLegacyFlagEnabled = this.props.domain?.flags.includes(nativeFcmLegacyFlag!)

        let hasWebEnabled
        let hasIOSEnabled
        let hasAndroidEnabled
        if (this.props.domain) {
            hasWebEnabled = isDeliveryChannelEnabled(this.props.domain, DeliveryChannel.WEB)
            hasIOSEnabled = isDeliveryChannelEnabled(this.props.domain, DeliveryChannel.NATIVE_IOS)
            hasAndroidEnabled = isDeliveryChannelEnabled(this.props.domain, DeliveryChannel.NATIVE_ANDROID)
        }

        return (
            <Well
                className="nested"
                title="Send Integrations"
                id="send-integrations"
                actions={
                    <Button disabled={this.props.loading} onClick={this.handleDrawerOpen} size="small">
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Well
                                title={
                                    <Space size={6}>
                                        <AppleFilled />
                                        <Typography.Text>Apple</Typography.Text>
                                    </Space>
                                }
                                className="nested"
                                mode="ghost"
                                footerMode="hidden"
                                padding="none"
                            >
                                <Flex vertical gap={8}>
                                    {hasWebEnabled && (
                                        <ApnsIntegrationWell
                                            apnsConfiguration={this.props.domain?.apnsConfiguration}
                                            channel={SendIntegrationChannel.WEB}
                                        />
                                    )}

                                    {hasIOSEnabled && (
                                        <ApnsIntegrationWell
                                            apnsConfiguration={this.props.domain?.nativeApnsConfiguration}
                                            channel={SendIntegrationChannel.NATIVE}
                                        />
                                    )}
                                </Flex>
                            </Well>

                            {hasAndroidEnabled && (
                                <>
                                    <Divider size="small" className="inner-well-divider" />

                                    <Well
                                        mode="ghost"
                                        footerMode="hidden"
                                        padding="none"
                                        title={
                                            <Space size={6}>
                                                <FirebaseIcon />
                                                <Typography.Text>Android</Typography.Text>
                                            </Space>
                                        }
                                    >
                                        <NativeFcmIntegrationWell
                                            fcmConfig={this.props.domain?.nativeFcmConfiguration}
                                        />
                                    </Well>
                                </>
                            )}
                        </>
                    )}

                    <SendIntegrationsDrawer
                        key={this.state.drawerKey}
                        visible={this.state.showDrawer}
                        onClose={this.handleDrawerClose}
                        domain={this.props.domain}
                        onSubmit={this.handleIntegrationsSubmit}
                        submitDisabled={this.submitDisabled}
                        nativeFcmLegacy={hasNativeFcmLegacyFlagEnabled}
                    />
                </Skeleton>
            </Well>
        )
    }

    protected handleDrawerOpen = async () => {
        return this.setState({
            showDrawer: true,
            drawerKey: randomstring.generate(),
        })
    }

    protected handleDrawerClose = async () => {
        this.setState({
            showDrawer: false,
            drawerKey: undefined,
        })

        const historyState = this.props.history.location.state
        if (historyState?.returnTo) {
            this.appSvc.routeWithinDomain(historyState.returnTo)
        }
    }

    protected submitDisabled = (integrations: DomainSendIntegrationModel) => {
        return Object.keys(getIntegrationsValuesUpdate(this.props.domain, integrations)).length === 0
    }

    protected handleIntegrationsSubmit = async (integrations: DomainSendIntegrationModel) => {
        const update = getIntegrationsValuesUpdate(this.props.domain, integrations)

        try {
            const res = await this.domainService.updateDomainById(this.props.domain.id, update)

            if (res.ok) {
                this.props.onDomainUpdated(res.data)

                return res
            }
        } catch (error) {
            simpleFormErrorNotification(error)
        }

        return { ok: false }
    }
}

export default withRouter(SendIntegrationsWell)
